import { useState } from 'react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import SEOHead from '../../components/seo/SEOHead';
import StructuredData from '../../components/seo/StructuredData';

const ContactPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || "https://example.com";
  const [activeForm, setActiveForm] = useState('general');
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    empresa: '',
    mensaje: '',
    servicio: '',
    presupuesto: '',
    urgencia: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent, formType: string) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const submitUrl = formType === 'general'
        ? 'https://readdy.ai/api/form/d47n80jq5e24j8usdbfg'
        : 'https://readdy.ai/api/form/d47n80jq5e24j8usdbg0';

      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) formDataToSend.append(key, value);
      });

      const response = await fetch(submitUrl, {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nombre: '',
          email: '',
          telefono: '',
          empresa: '',
          mensaje: '',
          servicio: '',
          presupuesto: '',
          urgencia: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      question: "¿Cuánto tiempo toma obtener una respuesta?",
      answer: "Garantizamos respuesta en menos de 24 horas para todos los mensajes recibidos durante días laborables. Para consultas urgentes, recomendamos usar WhatsApp o agendar una llamada directa."
    },
    {
      question: "¿La consulta inicial es realmente gratuita?",
      answer: "Sí, ofrecemos 30 minutos de consulta completamente gratuita donde evaluamos tu situación y te proporcionamos un plan inicial personalizado. No hay compromisos ni costos ocultos."
    },
    {
      question: "¿Trabajan con clientes fuera de Miami?",
      answer: "Absolutamente. Aunque nuestra oficina está en Miami Beach, trabajamos con clientes en todo Estados Unidos y internacionalmente a través de consultas virtuales y servicios remotos."
    },
    {
      question: "¿Qué información necesito para la consulta?",
      answer: "Para maximizar el valor de tu consulta, ten a mano información sobre tu situación financiera actual, objetivos de negocio, y cualquier documentación relevante como reportes de crédito o estados financieros."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <SEOHead
        title="Contacto CashFlow VIP Miami | Consulta Gratuita Servicios Financieros"
        description="Contacta a CashFlow VIP en Miami Beach. Consulta gratuita de 30 minutos, respuesta garantizada en 24h. Oficina: 936 71st St, Miami Beach, FL. ¡Transforma tus finanzas hoy!"
        keywords="contacto CashFlow VIP, consulta gratuita, Miami Beach, servicios financieros, asesoría financiera"
        canonical={`${siteUrl}/contacto`}
      />

      <StructuredData
        type="WebPage"
        data={{
          title: "Contacto",
          description: "Estamos aquí para ayudarte a transformar tu futuro financiero. Conecta con nuestros expertos y descubre cómo podemos impulsar tu éxito.",
          path: "/contacto"
        }}
      />

      <StructuredData
        type="FAQPage"
        data={{ faqs }}
      />

      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-gradient-to-br from-black via-gray-900 to-black">
          <div className="absolute inset-0 bg-gradient-to-r from-vip-gold/5 to-transparent"></div>
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-vip-gold to-yellow-300 bg-clip-text text-transparent">
                Contáctanos
              </h1>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Estamos aquí para ayudarte a transformar tu futuro financiero.
                Conecta con nuestros expertos y descubre cómo podemos impulsar tu éxito.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
                <div className="text-center">
                  <div className="w-16 h-16 bg-vip-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-phone-line text-2xl text-vip-gold"></i>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Respuesta Rápida</h3>
                  <p className="text-gray-400">Contestamos en menos de 24 horas</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-vip-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-2xl text-vip-gold"></i>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">100% Confidencial</h3>
                  <p className="text-gray-400">Tu información está completamente segura</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-vip-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-user-star-line text-2xl text-vip-gold"></i>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Expertos VIP</h3>
                  <p className="text-gray-400">Asesoría personalizada de alto nivel</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-16 bg-gray-900">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="text-3xl font-bold mb-8 text-white">Información de Contacto</h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-vip-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-xl text-vip-gold"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Oficina Principal</h3>
                      <p className="text-gray-300">936 71st St, Miami Beach, FL 33141</p>
                      <p className="text-gray-400 text-sm mt-1">Atención solo con cita previa</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-vip-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-xl text-vip-gold"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Teléfono</h3>
                      <p className="text-gray-300">+1 (305) 555-CASH</p>
                      <p className="text-gray-400 text-sm mt-1">Lun - Vie: 9:00 AM - 7:00 PM EST</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-vip-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-xl text-vip-gold"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Email</h3>
                      <p className="text-gray-300">info@cashflow.vip</p>
                      <p className="text-gray-400 text-sm mt-1">Respuesta garantizada en 24h</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-vip-gold/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-xl text-vip-gold"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">Horarios de Atención</h3>
                      <p className="text-gray-300">Lunes - Viernes: 9:00 AM - 7:00 PM</p>
                      <p className="text-gray-300">Sábados: 10:00 AM - 4:00 PM</p>
                      <p className="text-gray-400 text-sm mt-1">Zona horaria: EST (Miami)</p>
                    </div>
                  </div>
                </div>

                {/* Social Media */}
                <div className="mt-12">
                  <h3 className="text-xl font-semibold mb-6">Síguenos</h3>
                  <div className="flex space-x-4">
                    <a href="https://www.skool.com/cashflow" target="_blank" rel="noopener noreferrer"
                      className="w-12 h-12 bg-gray-800 hover:bg-vip-gold/20 rounded-lg flex items-center justify-center transition-all duration-300 group">
                      <i className="ri-graduation-cap-line text-xl text-gray-400 group-hover:text-vip-gold"></i>
                    </a>
                    <a href="#" className="w-12 h-12 bg-gray-800 hover:bg-vip-gold/20 rounded-lg flex items-center justify-center transition-all duration-300 group">
                      <i className="ri-instagram-line text-xl text-gray-400 group-hover:text-vip-gold"></i>
                    </a>
                    <a href="#" className="w-12 h-12 bg-gray-800 hover:bg-vip-gold/20 rounded-lg flex items-center justify-center transition-all duration-300 group">
                      <i className="ri-linkedin-line text-xl text-gray-400 group-hover:text-vip-gold"></i>
                    </a>
                    <a href="#" className="w-12 h-12 bg-gray-800 hover:bg-vip-gold/20 rounded-lg flex items-center justify-center transition-all duration-300 group">
                      <i className="ri-youtube-line text-xl text-gray-400 group-hover:text-vip-gold"></i>
                    </a>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div>
                <h2 className="text-3xl font-bold mb-8 text-white">Acciones Rápidas</h2>
                <div className="space-y-6">
                  <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-vip-gold/30 transition-all duration-300">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-12 h-12 bg-vip-gold/20 rounded-lg flex items-center justify-center">
                        <i className="ri-calendar-check-line text-xl text-vip-gold"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">Agendar Consulta Gratuita</h3>
                        <p className="text-gray-400 text-sm">30 minutos de asesoría sin costo</p>
                      </div>
                    </div>
                    <a href="https://web.cashflow.vip/widget/booking/dZafhFlPIO82YbvGywB0"
                      target="_blank" rel="noopener noreferrer"
                      className="w-full bg-vip-gold hover:bg-yellow-500 text-black font-semibold py-3 px-6 rounded-lg transition-all duration-300 inline-block text-center whitespace-nowrap">
                      Agendar Ahora
                    </a>
                  </div>

                  <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-vip-gold/30 transition-all duration-300">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-12 h-12 bg-vip-gold/20 rounded-lg flex items-center justify-center">
                        <i className="ri-group-line text-xl text-vip-gold"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">Únete a la Comunidad</h3>
                        <p className="text-gray-400 text-sm">Acceso exclusivo a Skool VIP</p>
                      </div>
                    </div>
                    <a href="https://www.skool.com/cashflow"
                      target="_blank" rel="noopener noreferrer"
                      className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 inline-block text-center whitespace-nowrap">
                      Acceder a Skool
                    </a>
                  </div>

                  <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-vip-gold/30 transition-all duration-300">
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-12 h-12 bg-vip-gold/20 rounded-lg flex items-center justify-center">
                        <i className="ri-whatsapp-line text-xl text-vip-gold"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold">WhatsApp Directo</h3>
                        <p className="text-gray-400 text-sm">Respuesta inmediata</p>
                      </div>
                    </div>
                    <a href="https://wa.me/13055552274"
                      target="_blank" rel="noopener noreferrer"
                      className="w-full bg-green-600 hover:bg-green-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 inline-block text-center whitespace-nowrap">
                      Escribir por WhatsApp
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Forms */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-vip-gold to-yellow-300 bg-clip-text text-transparent">
                  Envíanos un Mensaje
                </h2>
                <p className="text-xl text-gray-300">
                  Completa el formulario y nos pondremos en contacto contigo
                </p>
              </div>

              {/* Form Tabs */}
              <div className="flex justify-center mb-8">
                <div className="bg-gray-900 rounded-lg p-1 flex">
                  <button
                    onClick={() => setActiveForm('general')}
                    className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 whitespace-nowrap ${activeForm === 'general'
                        ? 'bg-vip-gold text-black'
                        : 'text-gray-300 hover:text-white'
                      }`}
                  >
                    Contacto General
                  </button>
                  <button
                    onClick={() => setActiveForm('consulta')}
                    className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 whitespace-nowrap ${activeForm === 'consulta'
                        ? 'bg-vip-gold text-black'
                        : 'text-gray-300 hover:text-white'
                      }`}
                  >
                    Solicitar Consulta
                  </button>
                </div>
              </div>

              {/* General Contact Form */}
              {activeForm === 'general' && (
                <form
                  onSubmit={(e) => handleSubmit(e, 'general')}
                  data-readdy-form
                  className="bg-gray-900 rounded-xl p-8 border border-gray-700"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="nombre" className="block text-sm font-medium text-gray-300 mb-2">
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-vip-gold transition-colors"
                        placeholder="Tu nombre completo"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-vip-gold transition-colors"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="telefono" className="block text-sm font-medium text-gray-300 mb-2">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-vip-gold transition-colors"
                        placeholder="+1 (305) 555-0123"
                      />
                    </div>
                    <div>
                      <label htmlFor="empresa" className="block text-sm font-medium text-gray-300 mb-2">
                        Empresa/Negocio
                      </label>
                      <input
                        type="text"
                        id="empresa"
                        name="empresa"
                        value={formData.empresa}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-vip-gold transition-colors"
                        placeholder="Nombre de tu empresa"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="mensaje" className="block text-sm font-medium text-gray-300 mb-2">
                      Mensaje *
                    </label>
                    <textarea
                      id="mensaje"
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleInputChange}
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-vip-gold transition-colors resize-none"
                      placeholder="Cuéntanos cómo podemos ayudarte..."
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1">Máximo 500 caracteres</p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="mb-6 p-4 bg-green-900/50 border border-green-500 rounded-lg">
                      <p className="text-green-300 text-sm">¡Mensaje enviado exitosamente! Te contactaremos pronto.</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mb-6 p-4 bg-red-900/50 border border-red-500 rounded-lg">
                      <p className="text-red-300 text-sm">Error al enviar el mensaje. Por favor intenta nuevamente.</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-vip-gold hover:bg-yellow-500 text-black font-semibold py-4 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}
                  </button>
                </form>
              )}

              {/* Consultation Request Form */}
              {activeForm === 'consulta' && (
                <form
                  onSubmit={(e) => handleSubmit(e, 'consulta')}
                  data-readdy-form
                  className="bg-gray-900 rounded-xl p-8 border border-gray-700"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="nombre" className="block text-sm font-medium text-gray-300 mb-2">
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        value={formData.nombre}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-vip-gold transition-colors"
                        placeholder="Tu nombre completo"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-vip-gold transition-colors"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="telefono" className="block text-sm font-medium text-gray-300 mb-2">
                        Teléfono *
                      </label>
                      <input
                        type="tel"
                        id="telefono"
                        name="telefono"
                        value={formData.telefono}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-vip-gold transition-colors"
                        placeholder="+1 (305) 555-0123"
                      />
                    </div>
                    <div>
                      <label htmlFor="servicio" className="block text-sm font-medium text-gray-300 mb-2">
                        Servicio de Interés *
                      </label>
                      <select
                        id="servicio"
                        name="servicio"
                        value={formData.servicio}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-vip-gold transition-colors pr-8"
                      >
                        <option value="">Selecciona un servicio</option>
                        <option value="creacion-llc">Creación de LLC</option>
                        <option value="reparacion-credito">Reparación de Crédito</option>
                        <option value="funding">Obtención de Funding</option>
                        <option value="mentoria">Mentoría VIP</option>
                        <option value="todos">Todos los servicios</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="presupuesto" className="block text-sm font-medium text-gray-300 mb-2">
                        Presupuesto Estimado
                      </label>
                      <select
                        id="presupuesto"
                        name="presupuesto"
                        value={formData.presupuesto}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-vip-gold transition-colors pr-8"
                      >
                        <option value="">Selecciona un rango</option>
                        <option value="menos-5k">Menos de $5,000</option>
                        <option value="5k-15k">$5,000 - $15,000</option>
                        <option value="15k-50k">$15,000 - $50,000</option>
                        <option value="mas-50k">Más de $50,000</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="urgencia" className="block text-sm font-medium text-gray-300 mb-2">
                        Urgencia del Proyecto
                      </label>
                      <select
                        id="urgencia"
                        name="urgencia"
                        value={formData.urgencia}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-vip-gold transition-colors pr-8"
                      >
                        <option value="">Selecciona urgencia</option>
                        <option value="inmediato">Inmediato (1-2 semanas)</option>
                        <option value="pronto">Pronto (1 mes)</option>
                        <option value="planificando">Planificando (2-3 meses)</option>
                        <option value="explorando">Solo explorando opciones</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="mensaje" className="block text-sm font-medium text-gray-300 mb-2">
                      Detalles Adicionales
                    </label>
                    <textarea
                      id="mensaje"
                      name="mensaje"
                      value={formData.mensaje}
                      onChange={handleInputChange}
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-vip-gold transition-colors resize-none"
                      placeholder="Cuéntanos más sobre tu situación actual y objetivos..."
                    ></textarea>
                    <p className="text-xs text-gray-400 mt-1">Máximo 500 caracteres</p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="mb-6 p-4 bg-green-900/50 border border-green-500 rounded-lg">
                      <p className="text-green-300 text-sm">¡Solicitud enviada exitosamente! Te contactaremos para agendar tu consulta gratuita.</p>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="mb-6 p-4 bg-red-900/50 border border-red-500 rounded-lg">
                      <p className="text-red-300 text-sm">Error al enviar la solicitud. Por favor intenta nuevamente.</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-vip-gold hover:bg-yellow-500 text-black font-semibold py-4 px-6 rounded-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? 'Enviando...' : 'Solicitar Consulta Gratuita'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-16 bg-gray-900">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4 text-white">Nuestra Ubicación</h2>
              <p className="text-gray-300">Visítanos en el corazón de Miami Beach</p>
            </div>
            <div className="max-w-4xl mx-auto">
              <div className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3592.2947!2d-80.1419!3d25.8473!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d9b48b1e6f5555%3A0x1234567890abcdef!2s936%2071st%20St%2C%20Miami%20Beach%2C%20FL%2033141!5e0!3m2!1sen!2sus!4v1699999999999!5m2!1sen!2sus"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="CashFlow VIP Location"
                ></iframe>
              </div>
              <div className="mt-6 text-center">
                <p className="text-gray-300 mb-2">
                  <strong>936 71st St, Miami Beach, FL 33141</strong>
                </p>
                <p className="text-gray-400 text-sm">
                  Estacionamiento disponible • Acceso para personas con discapacidad
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-vip-gold to-yellow-300 bg-clip-text text-transparent">
                  Preguntas Frecuentes
                </h2>
                <p className="text-gray-300">Respuestas a las consultas más comunes</p>
              </div>

              <div className="space-y-6">
                <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold mb-3 text-white">¿Cuánto tiempo toma obtener una respuesta?</h3>
                  <p className="text-gray-300">Garantizamos respuesta en menos de 24 horas para todos los mensajes recibidos durante días laborables. Para consultas urgentes, recomendamos usar WhatsApp o agendar una llamada directa.</p>
                </div>

                <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold mb-3 text-white">¿La consulta inicial es realmente gratuita?</h3>
                  <p className="text-gray-300">Sí, ofrecemos 30 minutos de consulta completamente gratuita donde evaluamos tu situación y te proporcionamos un plan inicial personalizado. No hay compromisos ni costos ocultos.</p>
                </div>

                <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold mb-3 text-white">¿Trabajan con clientes fuera de Miami?</h3>
                  <p className="text-gray-300">Absolutamente. Aunque nuestra oficina está en Miami Beach, trabajamos con clientes en todo Estados Unidos y internacionalmente a través de consultas virtuales y servicios remotos.</p>
                </div>

                <div className="bg-gray-900 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold mb-3 text-white">¿Qué información necesito para la consulta?</h3>
                  <p className="text-gray-300">Para maximizar el valor de tu consulta, ten a mano información sobre tu situación financiera actual, objetivos de negocio, y cualquier documentación relevante como reportes de crédito o estados financieros.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
